// /admin/billboards — Bible Belt billboard Command Center.
// Token-gated via same X-Admin-Token used on /admin (sessionStorage).
// Five tabs: Markets · Creatives · Tracking #'s · Outreach · Campaigns

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  Download,
  ExternalLink,
  Image as ImageIcon,
  Loader2,
  Mail,
  MapPin,
  Phone,
  PhoneOff,
  Plus,
  Rocket,
  Send,
  Sparkles,
  Trash2,
  TrendingUp,
  Zap,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "callgod_admin_token";

const TABS = [
  { id: "markets", label: "Markets", icon: MapPin },
  { id: "creatives", label: "Creatives", icon: ImageIcon },
  { id: "numbers", label: "Tracking #'s", icon: Phone },
  { id: "outreach", label: "Outreach", icon: Mail },
  { id: "campaigns", label: "Campaigns", icon: Sparkles },
  { id: "launch", label: "Launch helper", icon: Rocket },
  { id: "attribution", label: "Attribution", icon: TrendingUp },
];

const useToken = () => {
  const [token, setTokenState] = useState(() => {
    try { return sessionStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
  });
  const setToken = useCallback((t) => {
    try { if (t) sessionStorage.setItem(TOKEN_KEY, t); else sessionStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
    setTokenState(t);
  }, []);
  return [token, setToken];
};

const apiFetch = (token, path, options = {}) =>
  fetch(`${API}${path}`, {
    ...options,
    headers: { "X-Admin-Token": token, "Content-Type": "application/json", ...(options.headers || {}) },
  });

// Mirror of backend MARKET_SLUGS — UI displays the vanity URL per market.
const VANITY_SLUGS = {
  springfield_mo: "springfield",
  branson_mo:     "branson",
  joplin_mo:      "joplin",
  tulsa_ok:       "tulsa",
  okc_ok:         "okc",
  jackson_ms:     "jackson",
  tupelo_ms:      "tupelo",
  birmingham_al:  "birmingham",
  montgomery_al:  "montgomery",
  knoxville_tn:   "knoxville",
  chattanooga_tn: "chattanooga",
  greenville_sc:  "greenville",
  little_rock_ar: "littlerock",
  shreveport_la:  "shreveport",
  baton_rouge_la: "batonrouge",
};

const SITE_ORIGIN = (() => {
  try { return window.location.origin; } catch { return ""; }
})();

// ── Markets tab ─────────────────────────────────────────────────────
function MarketsTab({ token, onProvision, onBulkProvision }) {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await apiFetch(token, "/admin/billboards/markets");
      const data = await res.json();
      if (alive) { setMarkets(data.markets || []); setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token]);

  if (loading) return <Loader2 className="mx-auto mt-12 h-6 w-6 animate-spin text-muted-foreground" />;

  return (
    <>
      <div className="cg-card mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl p-4">
        <div>
          <p className="text-[13px] font-medium text-foreground">Bible Belt test launch</p>
          <p className="text-[12px] text-muted-foreground">
            One click provisions tracking numbers in Tupelo MS · Jackson MS · Joplin MO · Shreveport LA (~$5/mo).
          </p>
        </div>
        <button
          type="button"
          data-testid="bb-bulk-provision-btn"
          onClick={onBulkProvision}
          className="cg-btn-primary !min-h-[36px] text-[13px]"
        >
          <span className="inline-flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" /> Provision 4 test markets</span>
        </button>
      </div>

      <div className="cg-card mt-4 overflow-x-auto rounded-2xl">
        <table className="w-full text-left text-[13px]" data-testid="bb-markets-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground" style={{ borderBottom: "1px solid hsla(38,25%,80%,0.6)" }}>
              <th className="px-3 py-3">Market</th>
              <th className="px-3 py-3">Tier</th>
              <th className="px-3 py-3">Pop</th>
              <th className="px-3 py-3">Static $/4-wk</th>
              <th className="px-3 py-3">Digital $/4-wk</th>
              <th className="px-3 py-3">Operator</th>
              <th className="px-3 py-3">Religiosity</th>
              <th className="px-3 py-3">Vanity URL</th>
              <th className="px-3 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {markets.map((m) => {
              const slug = VANITY_SLUGS[m.key];
              const vanityUrl = slug && SITE_ORIGIN ? `${SITE_ORIGIN}/m/${slug}` : null;
              return (
                <tr key={m.key} style={{ borderBottom: "1px solid hsla(38,25%,85%,0.5)" }}>
                  <td className="px-3 py-3 align-top">
                    <p className="font-medium text-foreground">{m.city}, {m.state}</p>
                    <p className="text-[11px] text-muted-foreground">{m.notes}</p>
                  </td>
                  <td className="px-3 py-3 align-top">
                    <span className="rounded-full px-2 py-0.5 text-[11px]" style={{ background: m.tier === 1 ? "hsla(110,40%,88%,0.9)" : m.tier === 2 ? "hsla(42,60%,88%,0.9)" : "hsla(20,40%,88%,0.9)", color: "hsl(20 30% 25%)" }}>T{m.tier}</span>
                  </td>
                  <td className="px-3 py-3 align-top text-foreground/85">{(m.pop / 1000).toFixed(0)}k</td>
                  <td className="px-3 py-3 align-top text-foreground/85">${m.static_low}–${m.static_high}</td>
                  <td className="px-3 py-3 align-top text-foreground/85">${m.digital_low}–${m.digital_high}</td>
                  <td className="px-3 py-3 align-top text-foreground/85">{m.primary_op}</td>
                  <td className="px-3 py-3 align-top text-foreground/85">{Math.round(m.religiosity * 100)}%</td>
                  <td className="px-3 py-3 align-top">
                    {vanityUrl ? (
                      <button
                        type="button"
                        title="Copy vanity URL"
                        onClick={() => { navigator.clipboard.writeText(vanityUrl); }}
                        data-testid={`bb-copy-vanity-${m.key}`}
                        className="font-mono text-[11px] text-muted-foreground hover:text-foreground"
                      >
                        /m/{slug} <Copy className="ml-1 inline h-3 w-3" />
                      </button>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 align-top">
                    <button data-testid={`bb-provision-${m.key}`} type="button" onClick={() => onProvision(m)} className="cg-btn-ghost !min-h-[32px] !px-3 text-[12px]">
                      <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> Provision #</span>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Creatives tab ───────────────────────────────────────────────────
function CreativesTab({ token }) {
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ concept: "", brand: "", size: "" });

  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await apiFetch(token, "/admin/billboards/creatives");
      const data = await res.json();
      if (alive) { setCreatives(data.creatives || []); setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token]);

  const filtered = useMemo(() => creatives.filter(c => (
    (!filter.concept || c.concept === filter.concept) &&
    (!filter.brand || c.brand === filter.brand) &&
    (!filter.size || c.size === filter.size)
  )), [creatives, filter]);

  const uniq = (k) => Array.from(new Set(creatives.map(c => c[k])));

  if (loading) return <Loader2 className="mx-auto mt-12 h-6 w-6 animate-spin text-muted-foreground" />;

  return (
    <>
      <div className="mt-5 flex flex-wrap gap-2 text-[12px]">
        {[["concept", "Concept"], ["brand", "Brand"], ["size", "Size"]].map(([k, l]) => (
          <select key={k} data-testid={`bb-filter-${k}`} value={filter[k]} onChange={(e) => setFilter(f => ({ ...f, [k]: e.target.value }))} className="rounded-lg border bg-white/70 px-2.5 py-1.5" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }}>
            <option value="">All {l}</option>
            {uniq(k).map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        ))}
        <p className="ml-auto text-muted-foreground">{filtered.length} of {creatives.length} creatives</p>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map(c => (
          <div key={`${c.concept}-${c.brand}-${c.size}`} className="cg-card rounded-2xl p-3">
            <img src={c.public_url} alt={`${c.concept} ${c.brand}`} className="w-full rounded-lg bg-black" loading="lazy" />
            <div className="mt-2 flex items-center justify-between text-[12px]">
              <div>
                <p className="font-medium text-foreground">{c.concept.replace(/_/g, " ")}</p>
                <p className="text-muted-foreground">{c.brand} · {c.size} · {c.kb}KB</p>
              </div>
              <a href={c.public_url} download className="cg-btn-ghost !min-h-[30px] !px-2.5 text-[11px]">Download</a>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Tracking numbers tab ────────────────────────────────────────────
function NumbersTab({ token, refresh }) {
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await apiFetch(token, "/admin/billboards/twilio/numbers");
      const data = await res.json();
      if (!cancelled) { setNumbers(data.numbers || []); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token, refresh, tick]);

  const release = async (sid) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Release this number? Twilio stops billing immediately and you lose call history attribution.")) return;
    await apiFetch(token, `/admin/billboards/twilio/${sid}`, { method: "DELETE" });
    setTick(t => t + 1);
  };

  if (loading) return <Loader2 className="mx-auto mt-12 h-6 w-6 animate-spin text-muted-foreground" />;

  if (numbers.length === 0) {
    return (
      <div className="cg-card mt-5 rounded-2xl p-6 text-center text-muted-foreground">
        <p>No tracking numbers provisioned yet.</p>
        <p className="mt-1 text-[12px]">Go to the Markets tab and click &ldquo;Provision #&rdquo; on any market.</p>
      </div>
    );
  }

  return (
    <div className="cg-card mt-5 overflow-x-auto rounded-2xl">
      <table className="w-full text-left text-[13px]" data-testid="bb-numbers-table">
        <thead>
          <tr className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground" style={{ borderBottom: "1px solid hsla(38,25%,80%,0.6)" }}>
            <th className="px-3 py-3">Phone</th>
            <th className="px-3 py-3">Label</th>
            <th className="px-3 py-3">Market</th>
            <th className="px-3 py-3">Brand</th>
            <th className="px-3 py-3">Calls</th>
            <th className="px-3 py-3">Created</th>
            <th className="px-3 py-3">Action</th>
          </tr>
        </thead>
        <tbody>
          {numbers.map(n => (
            <tr key={n.sid} style={{ borderBottom: "1px solid hsla(38,25%,85%,0.5)" }}>
              <td className="px-3 py-3 font-mono text-foreground">{n.phone}</td>
              <td className="px-3 py-3 text-foreground/85">{n.label}</td>
              <td className="px-3 py-3 text-muted-foreground">{n.market_key}</td>
              <td className="px-3 py-3 text-muted-foreground">{n.brand}</td>
              <td className="px-3 py-3 font-medium text-foreground">{n.call_count}</td>
              <td className="px-3 py-3 text-muted-foreground">{new Date(n.created_at).toLocaleDateString()}</td>
              <td className="px-3 py-3">
                <button type="button" onClick={() => release(n.sid)} className="cg-btn-ghost !min-h-[30px] !px-2.5 text-[12px]" data-testid={`bb-release-${n.sid}`}>
                  <span className="inline-flex items-center gap-1"><PhoneOff className="h-3 w-3" /> Release</span>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Outreach tab ────────────────────────────────────────────────────
function OutreachTab({ token }) {
  const [markets, setMarkets] = useState([]);
  const [log, setLog] = useState([]);
  const [contact, setContact] = useState(null);
  const [form, setForm] = useState({
    market_key: "jackson_ms",
    operator: "Lamar",
    contact_name: "",
    brand: "callgod",
    desired_format: "both",
    desired_duration_weeks: 4,
    target_budget_usd: 500,
  });
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [sendForm, setSendForm] = useState({ to: "", reply_to: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      const [mRes, lRes] = await Promise.all([
        apiFetch(token, "/admin/billboards/markets"),
        apiFetch(token, "/admin/billboards/outreach/log"),
      ]);
      const m = await mRes.json();
      const l = await lRes.json();
      setMarkets(m.markets || []);
      setLog(l.outreach || []);
    })();
  }, [token]);

  // Pull suggested operator contact whenever market or operator changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const url = `/admin/billboards/operator_contact?market_key=${encodeURIComponent(form.market_key)}&operator=${encodeURIComponent(form.operator)}`;
      const res = await apiFetch(token, url);
      if (!res.ok) return;
      const data = await res.json();
      if (cancelled) return;
      setContact(data);
      // Only auto-fill if user hasn't typed their own recipient yet.
      setSendForm(s => s.to ? s : { ...s, to: data.suggested_to || "" });
    })();
    return () => { cancelled = true; };
  }, [token, form.market_key, form.operator]);

  const generate = async () => {
    setBusy(true); setMsg("");
    try {
      const res = await apiFetch(token, "/admin/billboards/outreach/draft", { method: "POST", body: JSON.stringify(form) });
      const data = await res.json();
      setDraft({ subject: data.subject, body: data.body });
      setMsg("Draft generated. Edit if needed, then send.");
    } finally { setBusy(false); }
  };

  const send = async () => {
    if (!sendForm.to) { setMsg("Enter the recipient email."); return; }
    setBusy(true); setMsg("");
    try {
      const res = await apiFetch(token, "/admin/billboards/outreach/send", {
        method: "POST",
        body: JSON.stringify({
          to: sendForm.to,
          reply_to: sendForm.reply_to || null,
          subject: draft.subject,
          body_text: draft.body,
          market_key: form.market_key,
          operator: form.operator,
        }),
      });
      if (res.status === 503) { setMsg("RESEND_API_KEY not configured in backend/.env. Add it then restart backend."); return; }
      if (!res.ok) { setMsg("Send failed. Check console."); return; }
      const data = await res.json();
      setMsg(`Sent ✓ (resend_id: ${data.resend_id || "?"})`);
      const lRes = await apiFetch(token, "/admin/billboards/outreach/log");
      setLog((await lRes.json()).outreach || []);
    } finally { setBusy(false); }
  };

  const downloadCsv = () => {
    // Token header can't ride on <a download>, so build a fetch-blob flow.
    apiFetch(token, "/admin/billboards/export.csv?kind=outreach")
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "billboards-outreach.csv";
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  return (
    <div className="mt-5 space-y-6">
      <div className="cg-card rounded-2xl p-5">
        <h3 className="font-serif-display text-lg text-foreground">1. Draft</h3>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="text-[12px]">Market
            <select value={form.market_key} data-testid="bb-out-market" onChange={(e) => setForm({ ...form, market_key: e.target.value })} className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }}>
              {markets.map(m => <option key={m.key} value={m.key}>{m.city}, {m.state}</option>)}
            </select>
          </label>
          <label className="text-[12px]">Operator
            <select value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })} className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }}>
              {["Lamar", "Outfront", "Clear Channel", "Adams Outdoor"].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <label className="text-[12px]">Brand
            <select value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }}>
              <option value="callgod">Call God</option>
              <option value="calljesus">Call Jesus</option>
            </select>
          </label>
          <label className="text-[12px]">Format
            <select value={form.desired_format} onChange={(e) => setForm({ ...form, desired_format: e.target.value })} className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }}>
              <option value="digital">Digital</option>
              <option value="static">Static</option>
              <option value="both">Both</option>
            </select>
          </label>
          <label className="text-[12px]">Contact name (optional)
            <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="e.g. Sarah" className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }} />
          </label>
          <label className="text-[12px]">Target $/board/4wk
            <input type="number" value={form.target_budget_usd} onChange={(e) => setForm({ ...form, target_budget_usd: Number(e.target.value) })} className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }} />
          </label>
        </div>
        {contact ? (
          <div className="mt-4 rounded-lg p-3 text-[12px]" style={{ background: "hsla(42,55%,94%,0.85)", border: "1px solid hsla(38,40%,75%,0.7)" }}>
            <p className="font-medium text-foreground">Suggested contact</p>
            <p className="text-muted-foreground">{contact.notes}</p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <span className="font-mono text-foreground">{contact.suggested_to}</span>
              <a href={contact.office_lookup_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-muted-foreground underline underline-offset-2 hover:text-foreground" data-testid="bb-out-lookup">
                Office lookup <ExternalLink className="h-3 w-3" />
              </a>
              <a href={contact.search_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-muted-foreground underline underline-offset-2 hover:text-foreground">
                Search reps <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        ) : null}
        <button type="button" disabled={busy} onClick={generate} className="cg-btn-primary mt-4 !min-h-[40px] text-[13px]" data-testid="bb-out-generate">
          <span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Generate draft</span>
        </button>
      </div>

      {draft.subject ? (
        <div className="cg-card rounded-2xl p-5">
          <h3 className="font-serif-display text-lg text-foreground">2. Review & send</h3>
          <label className="mt-3 block text-[12px]">Subject
            <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }} data-testid="bb-out-subject" />
          </label>
          <label className="mt-3 block text-[12px]">Body
            <textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={16} className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 font-mono text-[12px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }} data-testid="bb-out-body" />
          </label>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="text-[12px]">To (operator email)
              <input value={sendForm.to} onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })} placeholder="sales@lamar.com" className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }} data-testid="bb-out-to" />
            </label>
            <label className="text-[12px]">Reply-to (your email, optional)
              <input value={sendForm.reply_to} onChange={(e) => setSendForm({ ...sendForm, reply_to: e.target.value })} placeholder="you@gmail.com" className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }} />
            </label>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button type="button" disabled={busy} onClick={send} className="cg-btn-primary !min-h-[40px] text-[13px]" data-testid="bb-out-send">
              <span className="inline-flex items-center gap-1.5"><Send className="h-3.5 w-3.5" /> Send via Resend</span>
            </button>
            <button type="button" onClick={() => { navigator.clipboard.writeText(`${draft.subject}\n\n${draft.body}`); setMsg("Copied to clipboard."); }} className="cg-btn-ghost !min-h-[40px] text-[13px]">
              <span className="inline-flex items-center gap-1.5"><Copy className="h-3.5 w-3.5" /> Copy</span>
            </button>
            {msg ? <p className="text-[12px] text-muted-foreground">{msg}</p> : null}
          </div>
        </div>
      ) : null}

      <div className="cg-card rounded-2xl p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-serif-display text-lg text-foreground">3. Outreach log</h3>
          <button
            type="button"
            onClick={downloadCsv}
            data-testid="bb-out-csv"
            className="cg-btn-ghost !min-h-[32px] !px-3 text-[12px]"
          >
            <span className="inline-flex items-center gap-1.5"><Download className="h-3.5 w-3.5" /> CSV</span>
          </button>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-[12px]" data-testid="bb-out-log">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground" style={{ borderBottom: "1px solid hsla(38,25%,80%,0.6)" }}>
                <th className="px-2 py-2">When</th>
                <th className="px-2 py-2">To</th>
                <th className="px-2 py-2">Market</th>
                <th className="px-2 py-2">Operator</th>
                <th className="px-2 py-2">Subject</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">Events</th>
              </tr>
            </thead>
            <tbody>
              {log.length === 0 ? (
                <tr><td colSpan={7} className="px-2 py-4 text-muted-foreground">No outreach sent yet.</td></tr>
              ) : log.map(o => (
                <tr key={o.id} style={{ borderBottom: "1px solid hsla(38,25%,85%,0.5)" }}>
                  <td className="px-2 py-2 whitespace-nowrap text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="px-2 py-2 text-foreground/85">{o.to}</td>
                  <td className="px-2 py-2 text-muted-foreground">{o.market_key}</td>
                  <td className="px-2 py-2 text-muted-foreground">{o.operator}</td>
                  <td className="px-2 py-2 text-foreground/85 max-w-[280px] truncate">{o.subject}</td>
                  <td className="px-2 py-2">{o.status}</td>
                  <td className="px-2 py-2">{(o.events || []).map(e => e.type).join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Campaigns tab ───────────────────────────────────────────────────
function CampaignsTab({ token }) {
  const [campaigns, setCampaigns] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    market_key: "jackson_ms",
    brand: "callgod",
    concept: "phone_booth",
    sizes: ["bulletin"],
    board_count: 1,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    monthly_spend_usd: 0,
    tracking_phone: "",
    notes: "",
    status: "planning",
  });

  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [cRes, mRes, nRes] = await Promise.all([
        apiFetch(token, "/admin/billboards/campaigns"),
        apiFetch(token, "/admin/billboards/markets"),
        apiFetch(token, "/admin/billboards/twilio/numbers"),
      ]);
      const cData = await cRes.json();
      const mData = await mRes.json();
      const nData = await nRes.json();
      if (!cancelled) {
        setCampaigns(cData.campaigns || []);
        setMarkets(mData.markets || []);
        setNumbers(nData.numbers || []);
      }
    })();
    return () => { cancelled = true; };
  }, [token, tick]);

  const save = async () => {
    await apiFetch(token, "/admin/billboards/campaigns", { method: "POST", body: JSON.stringify(form) });
    setShowForm(false);
    setTick(t => t + 1);
  };

  const remove = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this campaign?")) return;
    await apiFetch(token, `/admin/billboards/campaigns/${id}`, { method: "DELETE" });
    setTick(t => t + 1);
  };

  return (
    <div className="mt-5">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-muted-foreground">{campaigns.length} campaigns</p>
        <button type="button" onClick={() => setShowForm(!showForm)} className="cg-btn-primary !min-h-[36px] text-[13px]" data-testid="bb-camp-new">
          <span className="inline-flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> New campaign</span>
        </button>
      </div>

      {showForm ? (
        <div className="cg-card mt-4 rounded-2xl p-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            <label className="text-[12px]">Market
              <select value={form.market_key} onChange={(e) => setForm({ ...form, market_key: e.target.value })} className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }}>
                {markets.map(m => <option key={m.key} value={m.key}>{m.city}, {m.state}</option>)}
              </select>
            </label>
            <label className="text-[12px]">Brand
              <select value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }}>
                <option value="callgod">Call God</option>
                <option value="calljesus">Call Jesus</option>
              </select>
            </label>
            <label className="text-[12px]">Concept
              <select value={form.concept} onChange={(e) => setForm({ ...form, concept: e.target.value })} className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }}>
                <option value="phone_booth">phone_booth — giant #</option>
                <option value="open_line">open_line — friendly</option>
                <option value="sign">sign — &ldquo;Looking for a sign?&rdquo;</option>
                <option value="talk_to_jesus">talk_to_jesus — direct</option>
              </select>
            </label>
            <label className="text-[12px]">Tracking #
              <select value={form.tracking_phone} onChange={(e) => setForm({ ...form, tracking_phone: e.target.value })} className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }}>
                <option value="">— none —</option>
                {numbers.map(n => <option key={n.sid} value={n.phone}>{n.phone} ({n.label})</option>)}
              </select>
            </label>
            <label className="text-[12px]">Start date
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }} />
            </label>
            <label className="text-[12px]">End date
              <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }} />
            </label>
            <label className="text-[12px]">$ / month
              <input type="number" value={form.monthly_spend_usd} onChange={(e) => setForm({ ...form, monthly_spend_usd: Number(e.target.value) })} className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }} />
            </label>
            <label className="text-[12px]">Status
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }}>
                {["planning", "live", "paused", "ended"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="text-[12px] md:col-span-3">Notes
              <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Lamar bulletin #12 on I-55" className="mt-1 w-full rounded-lg border bg-white/70 px-2.5 py-2 text-[13px]" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }} />
            </label>
          </div>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={save} className="cg-btn-primary !min-h-[36px] text-[13px]" data-testid="bb-camp-save">Save</button>
            <button type="button" onClick={() => setShowForm(false)} className="cg-btn-ghost !min-h-[36px] text-[13px]">Cancel</button>
          </div>
        </div>
      ) : null}

      <div className="cg-card mt-4 overflow-x-auto rounded-2xl">
        <table className="w-full text-left text-[13px]" data-testid="bb-campaigns-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground" style={{ borderBottom: "1px solid hsla(38,25%,80%,0.6)" }}>
              <th className="px-3 py-3">Market</th>
              <th className="px-3 py-3">Brand</th>
              <th className="px-3 py-3">Concept</th>
              <th className="px-3 py-3">Dates</th>
              <th className="px-3 py-3">$/mo</th>
              <th className="px-3 py-3">Tracking #</th>
              <th className="px-3 py-3">Calls</th>
              <th className="px-3 py-3">$/call</th>
              <th className="px-3 py-3">Status</th>
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {campaigns.length === 0 ? (
              <tr><td colSpan={10} className="px-3 py-5 text-muted-foreground">No campaigns. Click &ldquo;New campaign&rdquo; above.</td></tr>
            ) : campaigns.map(c => (
              <tr key={c.id} style={{ borderBottom: "1px solid hsla(38,25%,85%,0.5)" }}>
                <td className="px-3 py-3 text-foreground/85">{c.market_key}</td>
                <td className="px-3 py-3 text-muted-foreground">{c.brand}</td>
                <td className="px-3 py-3 text-muted-foreground">{c.concept}</td>
                <td className="px-3 py-3 text-muted-foreground">{c.start_date}{c.end_date ? ` → ${c.end_date}` : ""}</td>
                <td className="px-3 py-3 text-foreground/85">${c.monthly_spend_usd}</td>
                <td className="px-3 py-3 font-mono text-[11px] text-muted-foreground">{c.tracking_phone || "—"}</td>
                <td className="px-3 py-3 font-medium text-foreground">{c.call_count}</td>
                <td className="px-3 py-3 text-foreground/85">{c.call_count > 0 ? `$${(c.monthly_spend_usd / c.call_count).toFixed(2)}` : "—"}</td>
                <td className="px-3 py-3">
                  <span className="rounded-full px-2 py-0.5 text-[11px]" style={{ background: c.status === "live" ? "hsla(110,40%,88%,0.9)" : c.status === "paused" ? "hsla(42,60%,88%,0.9)" : "hsla(20,15%,90%,0.9)" }}>{c.status}</span>
                </td>
                <td className="px-3 py-3">
                  <button type="button" onClick={() => remove(c.id)} className="cg-btn-ghost !min-h-[28px] !px-2 text-[11px]" data-testid={`bb-camp-del-${c.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Launch helper tab (Blip + Lamar zip-finder + vanity URLs) ───────
function LaunchTab({ token }) {
  const [data, setData] = useState(null);
  const [markets, setMarkets] = useState([]);

  useEffect(() => {
    (async () => {
      const [b, m] = await Promise.all([
        apiFetch(token, "/admin/billboards/blip_plan").then(r => r.json()),
        apiFetch(token, "/admin/billboards/markets").then(r => r.json()),
      ]);
      setData(b); setMarkets(m.markets || []);
    })();
  }, [token]);

  if (!data) return <Loader2 className="mx-auto mt-12 h-6 w-6 animate-spin text-muted-foreground" />;

  const marketByKey = Object.fromEntries(markets.map(m => [m.key, m]));
  const recommended = new Set(data.recommended_test_markets || []);
  // Tier 1 markets first, then 2, then 3 — keep recommended on top.
  const ordered = [...data.presets].sort((a, b) => {
    const ra = recommended.has(a.market_key) ? 0 : 1;
    const rb = recommended.has(b.market_key) ? 0 : 1;
    if (ra !== rb) return ra - rb;
    return (marketByKey[a.market_key]?.tier || 9) - (marketByKey[b.market_key]?.tier || 9);
  });

  return (
    <div className="mt-5 space-y-6">
      <div className="cg-card rounded-2xl p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Self-serve</p>
            <h3 className="font-serif-display text-xl text-foreground">Blip Billboards launch</h3>
          </div>
          <a
            href={data.blip_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="bb-launch-blip-cta"
            className="cg-btn-primary !min-h-[36px] text-[13px]"
          >
            <span className="inline-flex items-center gap-1.5"><Rocket className="h-3.5 w-3.5" /> Open Blip</span>
          </a>
        </div>
        <ol className="mt-4 list-decimal space-y-1.5 pl-5 text-[13px] text-foreground/85">
          {(data.guidance || []).map((g, i) => (<li key={i}>{g.replace(/^\d+\.\s*/, "")}</li>))}
        </ol>
      </div>

      <div className="cg-card overflow-x-auto rounded-2xl">
        <table className="w-full text-left text-[13px]" data-testid="bb-launch-presets">
          <thead>
            <tr className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground" style={{ borderBottom: "1px solid hsla(38,25%,80%,0.6)" }}>
              <th className="px-3 py-3">Market</th>
              <th className="px-3 py-3">Tier</th>
              <th className="px-3 py-3">Daily budget</th>
              <th className="px-3 py-3">Zip codes (paste into Blip)</th>
              <th className="px-3 py-3">Vanity URL</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map(p => {
              const m = marketByKey[p.market_key] || {};
              const slug = VANITY_SLUGS[p.market_key];
              const vanityUrl = slug && SITE_ORIGIN ? `${SITE_ORIGIN}/m/${slug}` : null;
              const zips = (p.zip_codes || []).join(", ");
              const star = recommended.has(p.market_key);
              return (
                <tr key={p.market_key} style={{ borderBottom: "1px solid hsla(38,25%,85%,0.5)" }}>
                  <td className="px-3 py-3 align-top">
                    <span className="font-medium text-foreground">{m.city}, {m.state}</span>
                    {star ? <span className="ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-medium" style={{ background: "hsla(42,80%,80%,0.85)", color: "hsl(30 50% 25%)" }}>recommended</span> : null}
                  </td>
                  <td className="px-3 py-3 align-top text-muted-foreground">T{m.tier || "?"}</td>
                  <td className="px-3 py-3 align-top font-medium text-foreground">${p.daily_budget_usd}/day</td>
                  <td className="px-3 py-3 align-top">
                    <button
                      type="button"
                      title="Copy zip codes"
                      onClick={() => { navigator.clipboard.writeText(zips); }}
                      className="text-left font-mono text-[12px] text-muted-foreground hover:text-foreground"
                      data-testid={`bb-copy-zips-${p.market_key}`}
                    >
                      {zips} <Copy className="ml-1 inline h-3 w-3" />
                    </button>
                  </td>
                  <td className="px-3 py-3 align-top">
                    {vanityUrl ? (
                      <button
                        type="button"
                        title="Copy vanity URL"
                        onClick={() => { navigator.clipboard.writeText(vanityUrl); }}
                        className="font-mono text-[12px] text-muted-foreground hover:text-foreground"
                      >
                        /m/{slug} <Copy className="ml-1 inline h-3 w-3" />
                      </button>
                    ) : (
                      <span className="text-[12px] text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="cg-card rounded-2xl p-5">
        <h3 className="font-serif-display text-lg text-foreground">Direct operators (static + long-form)</h3>
        <p className="mt-1 text-[12px] text-muted-foreground">
          Use the Outreach tab to send a ready-drafted email. Find a local rep here first.
        </p>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          {[
            { name: "Lamar", url: "https://www.lamar.com/office-locator" },
            { name: "Outfront", url: "https://www.outfrontmedia.com/contact-us" },
            { name: "Clear Channel", url: "https://www.clearchanneloutdoor.com/contact" },
            { name: "Adams Outdoor", url: "https://www.adamsoutdoor.com/contact" },
          ].map(op => (
            <a
              key={op.name}
              href={op.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`bb-launch-op-${op.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex items-center justify-between rounded-lg px-3 py-2.5 text-[13px]"
              style={{ background: "hsla(42,55%,94%,0.85)", border: "1px solid hsla(38,40%,75%,0.7)" }}
            >
              <span className="font-medium text-foreground">{op.name}</span>
              <span className="inline-flex items-center gap-1 text-muted-foreground"><ExternalLink className="h-3.5 w-3.5" /> Locator</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Attribution tab ─────────────────────────────────────────────────
function AttributionTab({ token }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      const res = await apiFetch(token, "/admin/billboards/attribution");
      setData(await res.json());
    })();
  }, [token]);

  const downloadCsv = (kind) => {
    apiFetch(token, `/admin/billboards/export.csv?kind=${kind}`)
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `billboards-${kind}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  if (!data) return <Loader2 className="mx-auto mt-12 h-6 w-6 animate-spin text-muted-foreground" />;

  return (
    <div className="mt-5 space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <button type="button" onClick={() => downloadCsv("attribution")} className="cg-btn-ghost !min-h-[32px] !px-3 text-[12px]" data-testid="bb-attr-csv-calls">
          <span className="inline-flex items-center gap-1.5"><Download className="h-3.5 w-3.5" /> Calls CSV</span>
        </button>
        <button type="button" onClick={() => downloadCsv("visits")} className="cg-btn-ghost !min-h-[32px] !px-3 text-[12px]" data-testid="bb-attr-csv-visits">
          <span className="inline-flex items-center gap-1.5"><Download className="h-3.5 w-3.5" /> Web visits CSV</span>
        </button>
        <button type="button" onClick={() => downloadCsv("campaigns")} className="cg-btn-ghost !min-h-[32px] !px-3 text-[12px]" data-testid="bb-attr-csv-campaigns">
          <span className="inline-flex items-center gap-1.5"><Download className="h-3.5 w-3.5" /> Campaigns CSV</span>
        </button>
      </div>

      <div className="cg-card rounded-2xl p-5">
        <h3 className="font-serif-display text-lg text-foreground">Calls + visits by market</h3>
        {data.by_market.length === 0 ? (
          <p className="mt-3 text-[13px] text-muted-foreground">No tracked calls or visits yet. Provision a number or share a vanity URL.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {data.by_market.map(m => (
              <div key={m.market_key} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white/40 px-3 py-2 text-[13px]">
                <span className="text-foreground/85">{m.market_key}</span>
                <span className="text-foreground">
                  <span className="font-medium">{m.calls} calls</span>
                  <span className="ml-3 text-muted-foreground">{m.visits || 0} web visits</span>
                  {m.by_brand && Object.keys(m.by_brand).length > 0 ? (
                    <span className="ml-3 text-[11px] text-muted-foreground">
                      ({Object.entries(m.by_brand).map(([b, c]) => `${b}: ${c}`).join(" · ")})
                    </span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="cg-card rounded-2xl p-5">
        <h3 className="font-serif-display text-lg text-foreground">Recent inbound calls</h3>
        {data.recent_calls.length === 0 ? (
          <p className="mt-3 text-[13px] text-muted-foreground">No calls received yet.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-[12px]" data-testid="bb-attr-recent">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground" style={{ borderBottom: "1px solid hsla(38,25%,80%,0.6)" }}>
                  <th className="px-2 py-2">When</th>
                  <th className="px-2 py-2">Tracking #</th>
                  <th className="px-2 py-2">From</th>
                  <th className="px-2 py-2">Market</th>
                  <th className="px-2 py-2">Brand</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_calls.map((c, i) => (
                  <tr key={`${c.ts}-${i}`} style={{ borderBottom: "1px solid hsla(38,25%,85%,0.5)" }}>
                    <td className="px-2 py-2 text-muted-foreground">{new Date(c.ts).toLocaleString()}</td>
                    <td className="px-2 py-2 font-mono">{c.tracking_phone}</td>
                    <td className="px-2 py-2 font-mono text-foreground/85">{c.caller_phone}</td>
                    <td className="px-2 py-2 text-muted-foreground">{c.market_key}</td>
                    <td className="px-2 py-2 text-muted-foreground">{c.brand}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="cg-card rounded-2xl p-5">
        <h3 className="font-serif-display text-lg text-foreground">Recent web visits (vanity URLs)</h3>
        {(!data.recent_visits || data.recent_visits.length === 0) ? (
          <p className="mt-3 text-[13px] text-muted-foreground">No /m/:slug visits yet.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-[12px]" data-testid="bb-attr-visits">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground" style={{ borderBottom: "1px solid hsla(38,25%,80%,0.6)" }}>
                  <th className="px-2 py-2">When</th>
                  <th className="px-2 py-2">Slug</th>
                  <th className="px-2 py-2">Market</th>
                  <th className="px-2 py-2">Brand</th>
                  <th className="px-2 py-2">Referrer</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_visits.map((v, i) => (
                  <tr key={`${v.ts}-${i}`} style={{ borderBottom: "1px solid hsla(38,25%,85%,0.5)" }}>
                    <td className="px-2 py-2 text-muted-foreground">{new Date(v.ts).toLocaleString()}</td>
                    <td className="px-2 py-2 font-mono">{v.slug}</td>
                    <td className="px-2 py-2 text-muted-foreground">{v.market_key}</td>
                    <td className="px-2 py-2 text-muted-foreground">{v.brand}</td>
                    <td className="px-2 py-2 max-w-[220px] truncate text-muted-foreground">{v.referrer || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────
export default function Billboards() {
  const [token, setToken] = useToken();
  const [input, setInput] = useState("");
  const [authError, setAuthError] = useState(false);
  const [activeTab, setActiveTab] = useState("markets");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!token) return;
    // Validate token with a cheap admin call
    apiFetch(token, "/admin/billboards/markets").then(r => {
      if (r.status === 401) { setAuthError(true); setToken(""); }
    });
  }, [token, setToken]);

  const provision = async (market) => {
    const brand = window.prompt(`Provision a tracking number in ${market.city}, ${market.state} for which brand? (callgod / calljesus)`, "callgod");
    if (!brand) return;
    const label = window.prompt(`Label this number:`, `${market.city} ${market.state} · ${brand}`);
    if (!label) return;
    const res = await apiFetch(token, "/admin/billboards/twilio/provision", {
      method: "POST",
      body: JSON.stringify({ market_key: market.key, brand, label }),
    });
    if (!res.ok) {
      const err = await res.json();
      alert(`Failed: ${JSON.stringify(err.detail || err)}`);
      return;
    }
    const data = await res.json();
    alert(`Provisioned ${data.phone} for ${data.label}\n\nSwitch to the "Tracking #'s" tab to see it.`);
    setRefreshKey(k => k + 1);
    setActiveTab("numbers");
  };

  const bulkProvision = async () => {
    // Two-step: preview first, then confirm.
    const previewRes = await apiFetch(token, "/admin/billboards/twilio/provision_bulk", {
      method: "POST",
      body: JSON.stringify({ confirm: false }),
    });
    if (!previewRes.ok) {
      const err = await previewRes.json();
      alert(`Preview failed: ${JSON.stringify(err.detail || err)}`);
      return;
    }
    const preview = await previewRes.json();
    const lines = (preview.markets || []).map(m => `  • ${m.city}, ${m.state} (area code ${m.area_code})`).join("\n");
    const msg = `Provision tracking numbers for these markets?\n\n${lines}\n\nEstimated cost: ~$${preview.estimated_total_monthly_usd}/month total.\n\nThis BILLS Twilio immediately.`;
    if (!window.confirm(msg)) return;
    const res = await apiFetch(token, "/admin/billboards/twilio/provision_bulk", {
      method: "POST",
      body: JSON.stringify({ confirm: true, brand: "callgod" }),
    });
    const data = await res.json();
    const okCount = (data.results || []).filter(r => r.ok).length;
    const failCount = (data.results || []).length - okCount;
    alert(`Provisioned ${okCount} number${okCount === 1 ? "" : "s"}${failCount ? ` · ${failCount} failed` : ""}\n\nSwitch to the "Tracking #'s" tab to see them.`);
    setRefreshKey(k => k + 1);
    setActiveTab("numbers");
  };

  if (!token) {
    return (
      <main data-testid="bb-gate" className="flex min-h-screen items-center justify-center px-5">
        <div className="cg-card w-full max-w-sm rounded-2xl p-7">
          <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Billboards Command Center</p>
          <h1 className="font-serif-display mt-2 text-2xl text-foreground">Admin access</h1>
          <form onSubmit={(e) => { e.preventDefault(); if (input.trim()) setToken(input.trim()); }} className="mt-5">
            <input data-testid="bb-token-input" type="password" value={input} onChange={(e) => setInput(e.target.value)} className="w-full rounded-xl border bg-white/70 px-3.5 py-2.5 text-sm" style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }} placeholder="Admin token" />
            {authError ? <p className="mt-2 text-[13px]" style={{ color: "hsl(0 60% 42%)" }}>Token not accepted.</p> : null}
            <button type="submit" data-testid="bb-token-submit" className="cg-btn-primary mt-4 w-full !min-h-[46px] text-sm">Enter</button>
          </form>
          <Link to="/" className="mt-4 inline-block text-[13px] text-muted-foreground underline underline-offset-4">Back to Call God</Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="bb-dashboard" className="mx-auto w-full max-w-6xl px-4 pb-16 pt-6 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-3 w-3" /> Admin
          </Link>
          <h1 className="font-serif-display mt-1 text-3xl text-foreground">Billboards Command Center</h1>
          <p className="text-[12px] text-muted-foreground">Bible Belt distribution · research · attribution</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-1 border-b" style={{ borderColor: "hsla(38,25%,80%,0.6)" }}>
        {TABS.map(t => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button key={t.id} type="button" data-testid={`bb-tab-${t.id}`} onClick={() => setActiveTab(t.id)} className="-mb-px px-3.5 py-2 text-[13px]" style={{ borderBottom: active ? "2px solid hsl(38, 50%, 45%)" : "2px solid transparent", color: active ? "hsl(38, 50%, 30%)" : "hsl(20, 10%, 40%)" }}>
              <span className="inline-flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" />{t.label}</span>
            </button>
          );
        })}
      </div>

      {activeTab === "markets" && <MarketsTab token={token} onProvision={provision} onBulkProvision={bulkProvision} />}
      {activeTab === "creatives" && <CreativesTab token={token} />}
      {activeTab === "numbers" && <NumbersTab token={token} refresh={refreshKey} />}
      {activeTab === "outreach" && <OutreachTab token={token} />}
      {activeTab === "campaigns" && <CampaignsTab token={token} />}
      {activeTab === "launch" && <LaunchTab token={token} />}
      {activeTab === "attribution" && <AttributionTab token={token} />}
    </main>
  );
}
